// The password policy, in one place. Every form that sets a password
// (create user, admin password change, self password change) uses these
// rules, so a change here applies everywhere.
import { helpers, sameAs } from "@vuelidate/validators";

export const MIN_LENGTH = 12;

// The allowed special characters (OWASP list), space included:
//  !"#$%&'()*+,-./:;<=>?@[\]^_`{|}~
export const SPECIAL_CHARACTERS = " !\"#$%&'()*+,-./:;<=>?@[\\]^_`{|}~";

const hasSpecial = (value) =>
  [...value].some((c) => SPECIAL_CHARACTERS.includes(c));

// Shown as a checklist under the password field, in this order
export const PASSWORD_RULES = [
  {
    id: "length",
    label: `At least ${MIN_LENGTH} characters`,
    test: (value) => value.length >= MIN_LENGTH,
  },
  {
    id: "upper",
    label: "An uppercase letter (A-Z)",
    test: (value) => /[A-Z]/.test(value),
  },
  {
    id: "lower",
    label: "A lowercase letter (a-z)",
    test: (value) => /[a-z]/.test(value),
  },
  {
    id: "number",
    label: "A number (0-9)",
    test: (value) => /[0-9]/.test(value),
  },
  {
    id: "special",
    label: "A special character, e.g. ! @ # $ % & * or a space",
    test: hasSpecial,
  },
];

// Only on the change-password page, where we know the current password
const differentRule = (currentPassword) => ({
  id: "different",
  label: "Different from your current password",
  test: (value) => value.length > 0 && value !== currentPassword,
});

// Each rule with whether the password passes it. Pass `currentPassword`
// (when known) to also require the new password to differ from it.
export function checkPassword(value, { currentPassword } = {}) {
  const password = value || "";
  const rules =
    currentPassword === undefined
      ? PASSWORD_RULES
      : [...PASSWORD_RULES, differentRule(currentPassword || "")];
  return rules.map((rule) => ({ ...rule, passed: rule.test(password) }));
}

export function meetsPasswordPolicy(value) {
  return checkPassword(value).every((rule) => rule.passed);
}

// Vuelidate validator. Empty values are left to `required`.
export const strongPassword = helpers.withMessage(
  "Password doesn't meet all the requirements below.",
  (value) => !helpers.req(value) || meetsPasswordPolicy(value),
);

// Vuelidate validator: the new password must not be the current one
// (withParams makes Vuelidate re-check when the current password changes)
export const differentFrom = (currentPassword) =>
  helpers.withMessage(
    "Your new password must be different from your current password.",
    helpers.withParams(
      { type: "differentFrom", currentPassword },
      (value) => !helpers.req(value) || value !== currentPassword,
    ),
  );

// Vuelidate validator for the "repeat password" field
export const matchesPassword = (password) =>
  helpers.withMessage("Passwords don't match.", sameAs(password));
